/**
 * proposalInsert — apply an ad-hoc draft proposal to its bound section.
 *
 * Chat "draft content" intent, Insert action. The proposal was drafted
 * by the pipeline in proposal mode (section SSOT untouched); inserting
 * it is the moment it becomes real, in BOTH places:
 *
 *   1. Word — ``insertSectionContent`` replaces the section body under
 *      its heading (same path the pipeline's own inserts use).
 *   2. SSOT — the section lifecycle is walked to ``inserted`` if needed
 *      (edits are only legal from inserted/edited), then the content
 *      lands via ``submitAgentRedraft`` with optimistic concurrency.
 *      A version conflict is refetched and retried once.
 *
 * The Word write goes first: if it fails nothing was changed anywhere;
 * if the SSOT write-back fails after a successful Word write, the
 * result says so (``ssotUpdated: false``) so the caller can surface
 * "inserted but not tracked" honestly.
 */

import { getSectionState, markSectionInserted, markSectionVerified, type RetrievalProvenanceItem } from "../../api/client";
import { insertSectionContent, refreshLiveDocFields } from "../../services/wordWriter";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import type { ProposalInsertTarget } from "../messageProjection";
import { sanitizeDraftForWord } from "./draftSanitizer";
import { submitAgentRedraft } from "./writeBackBridge";

export interface ProposalInsertResult {
  ok: boolean;
  wordInserted: boolean;
  ssotUpdated: boolean;
  /** Writer-readable outcome note (also used for the approval EventLog entry). */
  message: string;
}

async function fetchSectionSnapshot(runId: string, moduleId: string) {
  const state = await getSectionState(runId);
  return state.sections.find((s) => s.module_id === moduleId) ?? null;
}

/** Walk the lifecycle to ``inserted`` when legal. Returns the latest
 *  known section_version, or null when no transition ran. Exported for
 *  useAdhocProposalFlow's no-pending-proposal fallback (direct
 *  version-checked edit when the proposal record vanished server-side). */
export async function walkToInserted(
  runId: string,
  moduleId: string,
  status: string,
): Promise<number | null> {
  let latestVersion: number | null = null;
  if (status === "drafted") {
    const v = await markSectionVerified(runId, moduleId);
    latestVersion = v.section_version;
    const i = await markSectionInserted(runId, moduleId);
    latestVersion = i.section_version;
  } else if (status === "verified") {
    const i = await markSectionInserted(runId, moduleId);
    latestVersion = i.section_version;
  }
  return latestVersion;
}

export interface ProposalInsertOptions {
  /** Caller already applied the change in Word (e.g. a span replace) —
   *  run only the SSOT half. */
  skipWordInsert?: boolean;
  /** Optimistic-concurrency version captured earlier by the caller.
   *  A lifecycle walk's fresher version still wins when one runs. */
  expectedVersion?: number;
  /** Retrieval provenance from an edit-highlight rewrite — forwarded to
   *  the SSOT write-back so it lands on the section's provenance. */
  transactions?: RetrievalProvenanceItem[];
  /** The rewrite agent's tool-call log + root span — forwarded so the calls
   *  land on Action History under the approval. */
  toolCalls?: Record<string, unknown>[];
  rootSpanId?: string | null;
  /** Chat edit-highlight trigger (user instruction + highlighted text) —
   *  forwarded so Action History heads the edit's subtree with the user input. */
  instruction?: string | null;
  selectedText?: string | null;
}

export async function insertProposalIntoSection(
  target: ProposalInsertTarget,
  content: string,
  options?: ProposalInsertOptions,
): Promise<ProposalInsertResult> {
  const cleaned = sanitizeDraftForWord(content);
  if (!cleaned.trim()) {
    return { ok: false, wordInserted: false, ssotUpdated: false, message: "Nothing to insert." };
  }

  // 1. Word write — section-targeted; falls out early on failure.
  if (!options?.skipWordInsert) {
    const sectionNumber = target.sectionNumber;
    if (!sectionNumber) {
      return {
        ok: false,
        wordInserted: false,
        ssotUpdated: false,
        message: "Couldn't resolve the section heading to insert under.",
      };
    }
    const inserted = await enqueueWordWrite(() =>
      insertSectionContent({
        sectionNumber,
        content: cleaned,
        scrollAfterInsert: true,
      }),
      `Writing §${sectionNumber}`,
    );
    if (!inserted) {
      return {
        ok: false,
        wordInserted: false,
        ssotUpdated: false,
        message: `Couldn't find §${target.sectionNumber} in the document — nothing was changed.`,
      };
    }
    // Captions and references in the inserted text take this document's numbers.
    await enqueueWordWrite(() => refreshLiveDocFields()).catch(() => 0);
  }

  // 2. SSOT write-back. Without a run binding the change is Word-only.
  if (!target.runId) {
    return {
      ok: true,
      wordInserted: true,
      ssotUpdated: false,
      message: `Inserted into §${target.sectionNumber} (no run context — change is not tracked).`,
    };
  }

  try {
    const snapshot = await fetchSectionSnapshot(target.runId, target.moduleId);
    if (!snapshot) {
      return {
        ok: true,
        wordInserted: true,
        ssotUpdated: false,
        message: `Inserted into §${target.sectionNumber}, but the section isn't tracked in this run.`,
      };
    }

    const walkedVersion = await walkToInserted(target.runId, target.moduleId, snapshot.status);
    let expectedVersion = walkedVersion ?? options?.expectedVersion ?? snapshot.section_version;

    // Provenance rides with the write-back. On a 409 the transition raises
    // before the server's append runs, so the retry (below) is the first
    // and only time these transactions are persisted — no duplication.
    let result = await submitAgentRedraft({
      runId: target.runId,
      moduleId: target.moduleId,
      content: cleaned,
      expectedVersion,
      sectionNumber: target.sectionNumber,
      transactions: options?.transactions,
      toolCalls: options?.toolCalls,
      rootSpanId: options?.rootSpanId,
      instruction: options?.instruction,
      selectedText: options?.selectedText,
    });
    if (!result.ok && result.conflict) {
      // One retry against the fresh version — a parallel write landed.
      expectedVersion = result.conflict.currentVersion;
      result = await submitAgentRedraft({
        runId: target.runId,
        moduleId: target.moduleId,
        content: cleaned,
        expectedVersion,
        sectionNumber: target.sectionNumber,
        transactions: options?.transactions,
        toolCalls: options?.toolCalls,
        rootSpanId: options?.rootSpanId,
        instruction: options?.instruction,
        selectedText: options?.selectedText,
      });
    }
    if (!result.ok) {
      return {
        ok: true,
        wordInserted: true,
        ssotUpdated: false,
        message:
          `Inserted into §${target.sectionNumber}, but the backend record wasn't updated` +
          `${result.error ? ` (${result.error})` : ""} — the downloaded doc may not include it.`,
      };
    }
    return {
      ok: true,
      wordInserted: true,
      ssotUpdated: true,
      message: `Ad-hoc draft inserted into §${target.sectionNumber}.`,
    };
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return {
      ok: true,
      wordInserted: true,
      ssotUpdated: false,
      message: `Inserted into §${target.sectionNumber}, but the backend update failed: ${detail}`,
    };
  }
}
